import GavelIcon from '@mui/icons-material/Gavel';
import { Avatar, Box, Container, Fab, Paper, Typography } from '@mui/material';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import './AboutUs.css';
import Footer from './Footer';
import LawAppBar from './LawAppBar';

export default function AboutUs() {
    const [cookies] = useCookies(['given_name', 'userid', 'picture', 'email', 'credential']);
    const navigate = useNavigate();

    return (
        <div className="App">
            <LawAppBar cookies={cookies} />
            <Container
                maxWidth="lg"
                sx={{
                    padding: { xs: '20px', sm: '40px' },
                    marginTop: '40px',
                    marginBottom: '60px'
                }}
            >
                {/* Hero Section */}
                <Box sx={{ textAlign: 'center', marginBottom: '60px' }}>
                    <Typography
                        variant="h3"
                        component="h1"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '2rem', sm: '2.8rem', md: '3.2rem' },
                            marginBottom: '1rem',
                            letterSpacing: '-0.02em',
                            lineHeight: 1.2
                        }}
                    >
                        Built by legal experts.
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: { xs: '1rem', sm: '1.2rem' },
                            fontWeight: 400,
                            fontStyle: 'italic'
                        }}
                    >
                        Powered by Seattle innovation.
                    </Typography>
                </Box>

                {/* Our Mission Section */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Our Mission
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '20px'
                        }}
                    >
                        Provide an accurate, affordable, and instantly accessible platform that empowers legal teams to work faster, reduce overhead, and secure better settlements for their clients.
                    </Typography>
                </Paper>

                {/* A Note from Our Founder */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '32px',
                            letterSpacing: '-0.01em',
                            textAlign: 'center'
                        }}
                    >
                        A Note from Our Founder
                    </Typography>

                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', md: 'row' },
                            alignItems: { xs: 'center', md: 'flex-start' },
                            gap: { xs: '24px', md: '32px' },
                            marginBottom: '32px'
                        }}
                    >
                        <Avatar
                            src="/rohit_photo.jpg"
                            alt="Rohit Kundaji"
                            sx={{
                                width: { xs: 120, sm: 150 },
                                height: { xs: 120, sm: 150 },
                                border: '3px solid var(--border-color)',
                                boxShadow: 'var(--shadow-light)'
                            }}
                        />
                        <Box sx={{ flex: 1, textAlign: { xs: 'center', md: 'left' } }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    lineHeight: 1.8,
                                    fontStyle: 'italic',
                                    marginBottom: '20px'
                                }}
                            >
                                "With a family of practicing attorneys, I see firsthand that the 'practice of law' often looks more like the 'practice of paperwork.' I see the fatigue of brilliant legal minds who spend their evenings manually auditing tedious documents and squinting at handwritten notes.
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    lineHeight: 1.8,
                                    fontStyle: 'italic',
                                    marginBottom: '20px'
                                }}
                            >
                                My career spans twenty years of engineering leadership, including significant tenures at Google, Meta, and Amazon. I’ve helped build the systems the world relies on every day—from the Chrome browser and Facebook to Amazon’s core infrastructure and Google’s professional suites.

                                Throughout these two decades, my focus has been to build for massive data scale and ironclad security, while keeping the interface so intuitive that it requires zero training.
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    lineHeight: 1.8,
                                    fontStyle: 'italic',
                                    marginBottom: '20px'
                                }}
                            >
                                I founded AttorneyAide.com to bring that 'Big Tech' standard home. We’ve combined the rigorous, HIPAA-grade security architecture with the seamless ease of use you expect from a consumer app. Our goal is to handle the tedious data hurdles, so you can get back to the work that matters: advocating for your clients."
                            </Typography>
                            <Typography
                                variant="body1"
                                sx={{
                                    color: 'var(--text-secondary)',
                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                    fontWeight: 500,
                                    marginTop: '24px',
                                    textAlign: 'right'
                                }}
                            >
                                — Rohit Kundaji, Founder & CEO, AttorneyAide.com
                            </Typography>
                        </Box>
                    </Box>
                </Paper>

                {/* The Minds Behind the Platform
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        The Minds Behind the Platform
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8
                        }}
                    >
                        Based in the heart of the Seattle tech corridor, we are a strategic collaboration between veteran personal injury attorneys, senior paralegals, and world-class software engineers.
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginTop: '20px'
                        }}
                    >
                        We built this tool because we lived the "grind"—the endless hours spent tabulating medical bills and manualizing chronologies. We combined legal intuition with cutting-edge AI to solve the specific bottlenecks of PI litigation.
                    </Typography>
                </Paper> */}

                {/* Transform Your Workflow */}
                {/* <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '24px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Transform Your Workflow
                    </Typography>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '24px'
                        }}
                    >
                        Stop the manual data entry and start practicing law. Use AttorneyAide.com to:
                    </Typography>
                    <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left' }}>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '16px',
                                '&::marker': {
                                    color: 'var(--accent-color)'
                                }
                            }}
                        >
                            <strong>Automate Data Extraction:</strong> Instantly pull critical data from chaotic medical records, provider bills, and complex police reports.
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '16px',
                                '&::marker': {
                                    color: 'var(--accent-color)'
                                }
                            }}
                        >
                            <strong>Visualize the Case:</strong> Generate comprehensive medical chronologies, itemized expense tables, and "at-a-glance" incident timelines.
                        </Typography>
                        <Typography
                            component="li"
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '16px',
                                '&::marker': {
                                    color: 'var(--accent-color)'
                                }
                            }}
                        >
                            <strong>Draft with Precision:</strong> Create high-quality Medical Timeline Narratives and comprehensive Demand Letters in a fraction of the usual time.
                        </Typography>
                    </Box>
                </Paper> */}

                {/* Three Core Pillars */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '8px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Our Three Core Pillars
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'var(--text-secondary)',
                            fontSize: '0.95rem',
                            marginBottom: '32px',
                            fontStyle: 'italic'
                        }}
                    >
                        We understand the stakes of your practice. Our platform is built on a foundation of trust and utility.
                    </Typography>

                    {/* Pillar 1 */}
                    <Box sx={{ marginBottom: '32px' }}>
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                                marginBottom: '12px'
                            }}
                        >
                            1. Instant Accessibility
                        </Typography>
                        <Typography variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px',
                                fontStyle: 'italic'
                            }}
                        >
                            No sales demos. No long-term contracts. No training or setup required.
                        </Typography>
                        <Typography variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                textAlign: 'left'
                            }}>
                            Sign in and start processing your first case in minutes. Our interface is so intuitive that if you can upload a PDF, you can use our platform.
                        </Typography>
                    </Box>

                    {/* Pillar 2 */}
                    <Box sx={{ marginBottom: '32px' }}>
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                                marginBottom: '12px'
                            }}
                        >
                            2. Verifiable Accuracy with AccuAide™
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px',
                                fontStyle: 'italic'
                            }}
                        >
                            We support your judgment; we don't replace it.
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                textAlign: 'left'
                            }}
                        >
                            A single clerical error can derail a settlement. That's why our software proactively finds and flags issues for your professional review. Further, every piece of information is linked back to the specific page in the original document. One click allows you to verify any entry, ensuring your filings remain beyond reproach.
                        </Typography>
                    </Box>

                    {/* Pillar 3 */}
                    <Box>
                        <Typography
                            variant="h5"
                            component="h3"
                            sx={{
                                color: 'var(--text-primary)',
                                fontWeight: 500,
                                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                                marginBottom: '12px'
                            }}
                        >
                            3. Ironclad Security
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '12px',
                                fontStyle: 'italic'
                            }}
                        >
                            Security — HIPAA-Grade & Big-Tech Hardened
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'var(--text-primary)',
                                fontSize: { xs: '1rem', sm: '1.1rem' },
                                lineHeight: 1.8,
                                marginBottom: '16px',
                                textAlign: 'left'
                            }}
                        >
                            Our architecture is designed by engineers with experience building secure systems at Google, Meta, and Amazon, protecting the personal data of billions of users. Your clients' sensitive data is protected by industry-leading security protocols:
                        </Typography>
                        <Box component="ul" sx={{ paddingLeft: { xs: '20px', sm: '24px' }, margin: 0, textAlign: 'left' }}>
                            <Typography
                                component="li"
                                variant="body1"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    lineHeight: 1.8,
                                    marginBottom: '12px'
                                }}
                            >
                                <strong>Zero-training Privacy:</strong> Zero Data Retention (ZDR) and contractual requirements to ensure no model training on Customer Data.
                            </Typography>
                            <Typography
                                component="li"
                                variant="body1"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    lineHeight: 1.8,
                                    marginBottom: '12px'
                                }}
                            >
                                <strong>Encryption:</strong> End-to-end encryption for data in transit (TLS 1.3+) and at rest (AES-256).
                            </Typography>
                            <Typography
                                component="li"
                                variant="body1"
                                sx={{
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '1rem', sm: '1.1rem' },
                                    lineHeight: 1.8,
                                    marginBottom: '12px'
                                }}
                            >
                                <strong>Authentication and Access Controls:</strong> Robust OAuth 2.0 protocols with support for multi-layered authentication (MFA). Role based access controls, logging, and audits.
                            </Typography>
                        </Box>
                    </Box>
                    <Typography
                        variant="body1"
                        sx={{
                            color: 'var(--text-primary)',
                            fontSize: { xs: '1rem', sm: '1.1rem' },
                            lineHeight: 1.8,
                            marginBottom: '16px',
                            textAlign: 'left'
                        }}
                    >
                        For more details see our <a href="/security" style={{ color: 'var(--text-primary)' }}>Security page</a>.
                    </Typography>
                </Paper>

                {/* Why PI Firms Choose Us - Comparison Table */}
                <Paper
                    elevation={0}
                    sx={{
                        padding: { xs: '32px', sm: '48px' },
                        borderRadius: '8px',
                        boxShadow: 'var(--shadow-light)',
                        border: '1px solid var(--border-color)',
                        overflow: 'hidden',
                        marginBottom: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 500,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '32px',
                            letterSpacing: '-0.01em',
                            textAlign: 'center'
                        }}
                    >
                        Why PI Firms Choose Us
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                            gap: { xs: '24px', sm: '32px' },
                            marginTop: '32px'
                        }}
                    >
                        {/* The Old Way */}
                        <Box>
                            <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                    color: 'var(--error-color)',
                                    fontWeight: 600,
                                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                                    marginBottom: '20px',
                                    paddingBottom: '12px',
                                    borderBottom: '2px solid var(--error-color)',
                                    textAlign: 'left'
                                }}
                            >
                                The Old Way
                            </Typography>
                            <Box component="ul" sx={{ paddingLeft: '20px', margin: 0, textAlign: 'left' }}>
                                <Typography
                                    component="li"
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                        lineHeight: 1.8,
                                        marginBottom: '16px',
                                        '&::marker': {
                                            color: 'var(--error-color)'
                                        }
                                    }}
                                >
                                    Weeks spent waiting for medical summaries
                                </Typography>
                                <Typography
                                    component="li"
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                        lineHeight: 1.8,
                                        marginBottom: '16px',
                                        '&::marker': {
                                            color: 'var(--error-color)'
                                        }
                                    }}
                                >
                                    Risk of missing a $10k bill in a 500-page stack
                                </Typography>
                                <Typography
                                    component="li"
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                        lineHeight: 1.8,
                                        marginBottom: '16px',
                                        '&::marker': {
                                            color: 'var(--error-color)'
                                        }
                                    }}
                                >
                                    Paralegal burnout from repetitive data entry
                                </Typography>
                                <Typography
                                    component="li"
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                        lineHeight: 1.8,
                                        '&::marker': {
                                            color: 'var(--error-color)'
                                        }
                                    }}
                                >
                                    Overhead-heavy "per hour" tasks
                                </Typography>
                            </Box>
                        </Box>

                        {/* The AttorneyAide Way */}
                        <Box>
                            <Typography
                                variant="h6"
                                component="h3"
                                sx={{
                                    color: 'var(--success-color)',
                                    fontWeight: 600,
                                    fontSize: { xs: '1.2rem', sm: '1.4rem' },
                                    marginBottom: '20px',
                                    paddingBottom: '12px',
                                    borderBottom: '2px solid var(--success-color)',
                                    textAlign: 'left'
                                }}
                            >
                                The AttorneyAide Way
                            </Typography>
                            <Box component="ul" sx={{ paddingLeft: '20px', margin: 0, textAlign: 'left' }}>
                                <Typography
                                    component="li"
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                        lineHeight: 1.8,
                                        marginBottom: '16px',
                                        '&::marker': {
                                            color: 'var(--success-color)'
                                        }
                                    }}
                                >
                                    First draft ready in minutes
                                </Typography>
                                <Typography
                                    component="li"
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                        lineHeight: 1.8,
                                        marginBottom: '16px',
                                        '&::marker': {
                                            color: 'var(--success-color)'
                                        }
                                    }}
                                >
                                    Automatic flagging of issues. One click verification.
                                </Typography>
                                <Typography
                                    component="li"
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                        lineHeight: 1.8,
                                        marginBottom: '16px',
                                        '&::marker': {
                                            color: 'var(--success-color)'
                                        }
                                    }}
                                >
                                    Paralegals focused on high-value case strategy
                                </Typography>
                                <Typography
                                    component="li"
                                    variant="body1"
                                    sx={{
                                        color: 'var(--text-primary)',
                                        fontSize: { xs: '0.95rem', sm: '1rem' },
                                        lineHeight: 1.8,
                                        '&::marker': {
                                            color: 'var(--success-color)'
                                        }
                                    }}
                                >
                                    Scalable; cost-effective.
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </Paper>

                {/* Call to Action */}
                <Box
                    sx={{
                        textAlign: 'center',
                        padding: { xs: '40px 20px', sm: '60px 40px' },
                        marginTop: '48px'
                    }}
                >
                    <Typography
                        variant="h4"
                        component="h2"
                        gutterBottom
                        sx={{
                            color: 'var(--text-primary)',
                            fontWeight: 400,
                            fontSize: { xs: '1.8rem', sm: '2.2rem' },
                            marginBottom: '32px',
                            letterSpacing: '-0.01em'
                        }}
                    >
                        Ready to reclaim your billable hours?
                    </Typography>
                    <Fab
                        variant="extended"
                        size="large"
                        color="primary"
                        onClick={() => navigate('/mycases')}
                        sx={{
                            background: 'var(--button-gradient)',
                            boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)',
                            fontWeight: 600,
                            textTransform: 'none',
                            fontSize: '1.05rem',
                            padding: '15px 28px',
                            borderRadius: '16px',
                            minHeight: '56px',
                            height: '56px',
                            lineHeight: '1.5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: 'auto',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            position: 'relative',
                            overflow: 'hidden',
                            border: 'none',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 12px 30px rgba(212, 175, 55, 0.6)',
                                background: 'var(--button-gradient-hover)'
                            },
                            '&:active': {
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 20px rgba(212, 175, 55, 0.4)'
                            },
                            '&::before': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: '-100%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                                transition: 'left 0.6s'
                            },
                            '&:hover::before': {
                                left: '100%'
                            }
                        }}
                    >
                        <GavelIcon sx={{ mr: 1, fontSize: '1.25rem' }} />
                        Try It Now
                    </Fab>
                </Box>
            </Container>
            <Footer />
        </div>
    );
}




